import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
} from "@chakra-ui/react";
import FileUploadSection from "./FileUploadSection";
import FileUpload from "./FileUpload";
import FileUploadStatus from "./FileUploadStatus";
import CreateTableFromFile from "./CreateTableFromFile";
import { getSignalRConnection } from "../config/signalRConnection";
import { HubConnectionState } from "@microsoft/signalr";

const FileUploadModal: React.FC<{ isOpen: boolean; onClose: () => void }> = ({
  isOpen,
  onClose,
}) => {
  const [userId, setUserId] = useState<string>("1");
  const [showCreateTable, setShowCreateTable] = useState(false);
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [showFileUploadStatus, setShowFileUploadStatus] = useState(false);
  const [uploadTableName, setUploadTableName] = useState<string | null>(null);
  const [connectionId, setConnectionId] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  useEffect(() => {
    setUserId(localStorage.getItem("email") ?? "1");
    setShowCreateTable(false);
    setShowFileUpload(false);
    setShowFileUploadStatus(false);
    setUploadTableName(null);
    setConnectionId(null);
    setSelectedFile(null);
  }, [isOpen]);

  const startSignalRAndRegister = async () => {
    const connection = getSignalRConnection();
    connection.off("UserConnected");

    return new Promise<string>(async (resolve, reject) => {
      const handleUserConnected = (msg: { connectionId: string }) => {
        setConnectionId(msg.connectionId);
        resolve(msg.connectionId);
      };

      connection.on("UserConnected", handleUserConnected);

      try {
        if (connection.state !== HubConnectionState.Connected) {
          await connection.start();
        }
        await connection.invoke("RegisterConnection", userId);
      } catch (err) {
        console.error("SignalR connection error:", err);
        reject(err);
      }
    });
  };

  const handleShowFileUpload = async (tableName: string) => {
    setUploadTableName(tableName);
    setShowCreateTable(false);
    setShowFileUploadStatus(false);
    setShowFileUpload(true);
    const connId = await startSignalRAndRegister();
    setConnectionId(connId);
  };

  const handleUploadComplete = (file: File) => {
    setSelectedFile(file);
    setShowFileUpload(false);
    setShowFileUploadStatus(true);
  };

  const handleBackToSection = () => {
    setUploadTableName(null);
    setConnectionId(null);
    setShowFileUpload(false);
    setShowFileUploadStatus(false);
    setShowCreateTable(false);
    setSelectedFile(null);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>File Upload</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {!showCreateTable && !showFileUpload && !showFileUploadStatus && (
            <FileUploadSection
              onShowCreateTable={() => setShowCreateTable(true)}
              onShowFileUpload={handleShowFileUpload}
            />
          )}

          {showFileUpload && connectionId && (
            <FileUpload
              userId={userId}
              tableName={uploadTableName ?? ""}
              connectionId={connectionId}
              onUploadComplete={handleUploadComplete}
              onCancel={handleBackToSection}
            />
          )}

          {showFileUploadStatus && connectionId && selectedFile && (
            <FileUploadStatus
              userId={userId}
              connectionId={connectionId}
              tableName={uploadTableName ?? ""}
              file={selectedFile}
            />
          )}

          {showCreateTable && (
            <CreateTableFromFile onBack={handleBackToSection} />
          )}

          {(showFileUpload || showFileUploadStatus || showCreateTable) && (
            <div style={{ textAlign: "center", marginTop: 8 }}>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  handleBackToSection();
                }}
                style={{ color: "#3182ce" }}
              >
                ← Back to Table List
              </a>
            </div>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default FileUploadModal;
